import { FormEvent, useEffect, useMemo, useState } from 'react';

import { SnapshotRepublicationPayload, TopologySubscription } from '../../../../../services/eventingApi';
import ConsumerSelect from '../shared/ConsumerSelect';
import { queueSubscriptions } from '../shared/subscriptions';
import { buttonClass, inputClass } from '../shared/ui';
import BackfillConfirmDialog from './BackfillConfirmDialog';
import { useSnapshotRepublication } from './useSnapshotRepublication';

const MAX_AGGREGATES = 500;

const parseIds = (text: string) => Array.from(new Set(text.split(/[\s,;]+/).map((id) => id.trim()).filter(Boolean)));

const BackfillForm = ({ subscriptions }: { subscriptions: TopologySubscription[] }) => {
  const targets = useMemo(() => queueSubscriptions(subscriptions), [subscriptions]);
  const [sourceService, setSourceService] = useState('');
  const [aggregateType, setAggregateType] = useState('');
  const [idsText, setIdsText] = useState('');
  const [target, setTarget] = useState(targets[0]?.consumer ?? '');
  const [pending, setPending] = useState<SnapshotRepublicationPayload | null>(null);
  const { submitting, request } = useSnapshotRepublication(() => {
    setIdsText('');
    setPending(null);
  });
  const ids = parseIds(idsText);
  const invalid = ids.length === 0 || ids.length > MAX_AGGREGATES || !target;

  useEffect(() => {
    setTarget((current) => (targets.some((subscription) => subscription.consumer === current) ? current : targets[0]?.consumer ?? ''));
  }, [targets]);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setPending({ sourceService, aggregateType, aggregateIds: ids, target });
  };

  return (
    <form className="grid gap-3 text-sm md:grid-cols-2 dark:text-gray-200" onSubmit={submit}>
      <label className="flex flex-col gap-1">
        Serviço dono (snake_case)
        <input className={inputClass} placeholder="collateral" value={sourceService} onChange={(change) => setSourceService(change.target.value)} required />
      </label>
      <label className="flex flex-col gap-1">
        Tipo de agregado
        <input className={inputClass} placeholder="Vehicle" value={aggregateType} onChange={(change) => setAggregateType(change.target.value)} required />
      </label>
      <label className="flex flex-col gap-1 md:col-span-2">
        aggregateIds (um por linha, vírgula ou espaço; até {MAX_AGGREGATES})
        <textarea className={`${inputClass} h-28 font-mono`} value={idsText} onChange={(change) => setIdsText(change.target.value)} required />
      </label>
      <ConsumerSelect targets={targets} value={target} onChange={setTarget} />
      <div className="flex items-end justify-end gap-3">
        <span className="text-xs text-gray-500 dark:text-gray-400">{ids.length} agregado(s)</span>
        <button type="submit" className={buttonClass.secondary} disabled={submitting || invalid}>
          Pedir snapshots
        </button>
      </div>
      <BackfillConfirmDialog request={pending} submitting={submitting} onClose={() => setPending(null)} onConfirm={request} />
    </form>
  );
};

export default BackfillForm;
